<template>
  <transition name="slide-fade" v-if="store.state.user_data.is_motom_internal_user">
    <div v-if="isVisible" class="queue-progress-container" :class="{ 'static': isStatic }">
      <div class="queue-progress-card" :class="`status-${currentStatus}`">
        <div class="icon-container">
          <div v-if="currentStatus === 'queue'" class="icon icon-queue">
            <svg-icon name="queue" />
          </div>
          <div v-else-if="currentStatus === 'processing'" class="icon icon-processing">
            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
              <path d="M12.4 13H14V14.3333H10V10.3333H11.3333V12.1533C12.5533 11.1733 13.3333 9.67997 13.3333 7.99997C13.3333 5.28664 11.2933 3.03997 8.66667 2.7133V1.36664C12.0333 1.69997 14.6667 4.53997 14.6667 7.99997C14.6667 9.9933 13.7867 11.78 12.4 13ZM2.66667 7.99997C2.66667 6.31997 3.44667 4.81997 4.66667 3.84664V5.66664H6V1.66664H2V2.99997H3.6C2.21334 4.21997 1.33334 6.00664 1.33334 7.99997C1.33334 11.46 3.96667 14.3 7.33334 14.6333V13.2866C4.70667 12.96 2.66667 10.7133 2.66667 7.99997Z" fill="currentColor"/>
            </svg>

            <svg viewBox="0 0 16 16" xmlns="http://www.w3.org/2000/svg">
              <path d="M10.8267 5.40664L7.05334 9.17997L5.16667 7.2933L4.22667 8.2333L7.05334 11.06L11.7667 6.34664L10.8267 5.40664Z" fill="currentColor" />
            </svg>
          </div>
          <div v-else-if="currentStatus === 'done'" class="icon icon-done">
            <svg-icon name="yes-2" />
          </div>
        </div>
        
        <div class="status-content">
          <h3 class="status-title">{{ statusTitle }}</h3>
          <p class="description">{{ statusDescription }}</p>
          <div v-if="currentStatus === 'processing' && progress > 0" class="progress-bar">
            <div class="progress-fill" :style="{ width: progress + '%' }"></div>
          </div>
        </div>
        
        <button class="close-btn" @click="handleClose" aria-label="Close">
          <svg-icon name="close" />
        </button>
      </div>
    </div>
  </transition>
  <!-- <span v-if="currentStatus === 'queue'" @click="testProgress">test queue Progress</span> -->

</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, getCurrentInstance } from 'vue';

const app = getCurrentInstance();
const ajax = app.appContext.config.globalProperties.$ajax;
const EventBus = app.appContext.config.globalProperties.EventBus;
const store = app.appContext.config.globalProperties.$store;

// console.log('QueueProgress mounted, EventBus:', EventBus);

const props = defineProps({
  isStatic: {
    type: Boolean,
    default: false,
  },
});

const isVisible = ref(false);
const currentStatus = ref('queue'); // queue, processing, done
const progress = ref(0);
const jobId = ref(null);
let pollingTimer = null;
let statusTimer = null;
let pollingCount = 0;
let emptyArrayCount = 0; // 连续空数组计数

const STORAGE_KEY = 'queue_progress_status';
const MAX_POLLING_COUNT = 300;
const TIMEOUT = 5000;

// Test function to simulate different progress states
// const testProgress = () => {
//   console.log('Testing queue progress...');
  
//   // Simulate triggering the ready-queue-update event using EventBus
//   EventBus.emit('ready-queue-update', { jobId: 'test-job-123' });
// };

const statusTitle = computed(() => {
  switch (currentStatus.value) {
    case 'queue':
      return 'In queue';
    case 'processing':
      return 'Processing';
    case 'done':
      return 'Creator records updated.';
    default:
      return '';
  }
});

const statusDescription = computed(() => {
  switch (currentStatus.value) {
    case 'queue':
      return 'Processing will begin shortly.';
    case 'processing':
      return progress.value > 0 ? `Updating creator records. ${progress.value}%` : 'Updating creator records.';
    case 'done':
      return '';
    default:
      return '';
  }
});

// Save state to localStorage
const saveState = () => {
  if (currentStatus.value === 'done') {
    // Don't save done state
    localStorage.removeItem(STORAGE_KEY);
    return;
  }
  
  const state = {
    isVisible: isVisible.value,
    currentStatus: currentStatus.value,
    progress: progress.value,
    jobId: jobId.value,
    pollingCount: pollingCount,
    timestamp: Date.now()
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

// Restore state from localStorage
const restoreState = () => {
  const savedState = localStorage.getItem(STORAGE_KEY);
  if (savedState) {
    try {
      const state = JSON.parse(savedState);
      // Check if state is expired (more than 1 hour)
      if (Date.now() - state.timestamp < 3600000) {
        isVisible.value = state.isVisible;
        currentStatus.value = state.currentStatus;
        progress.value = state.progress;
        jobId.value = state.jobId;
        pollingCount = state.pollingCount || 0;
        
        // If processing, continue polling
        if (currentStatus.value === 'processing' && jobId.value) {
          startPolling(false); // Don't reset count when restoring
        }
        
        // If queue, schedule the transition to processing
        if (currentStatus.value === 'queue' && jobId.value) {
          statusTimer = setTimeout(() => {
            currentStatus.value = 'processing';
            saveState();
            startPolling(false); // Don't reset count when restoring
          }, 800);
        }
      } else {
        localStorage.removeItem(STORAGE_KEY);
      }
    } catch (e) {
      console.error('Failed to restore queue status:', e);
      localStorage.removeItem(STORAGE_KEY);
    }
  }
};

// Clear all timers
const clearTimers = () => {
  if (pollingTimer) {
    clearInterval(pollingTimer);
    pollingTimer = null;
  }
  if (statusTimer) {
    clearTimeout(statusTimer);
    statusTimer = null;
  }
};

// Reset state
const resetState = () => {
  clearTimers();
  isVisible.value = false;
  currentStatus.value = 'queue';
  progress.value = 0;
  jobId.value = null;
  pollingCount = 0;
  emptyArrayCount = 0;
  localStorage.removeItem(STORAGE_KEY);
};

// Start polling
const startPolling = async (resetCount = true) => {
  if (!jobId.value) return;
  
  if (resetCount) {
    pollingCount = 0; // Reset counter only if specified
  }
  
  const checkStatus = async () => {
    // Check if max polling count reached
    if (pollingCount >= MAX_POLLING_COUNT) {
      console.warn(`Max polling count (${MAX_POLLING_COUNT}) reached. Stopping polling.`);
      clearTimers();
      return;
    }

    pollingCount++;

    try {
      const response = await ajax.get('/get-batch-job-status', {
        params: { job_id: jobId.value }
      });
      const arr = response?.data?.data?.data;
      if (!Array.isArray(arr) || arr.length === 0) {
        emptyArrayCount ++;
        if (emptyArrayCount >= 2) {
          // if empty array received 2 times consecutively, stop polling
          resetState();
          return;
        }

        // continue polling
        return;
      } else {
        emptyArrayCount = 0; // if valid data received, reset counter
      }
      const data = arr[0];
      progress.value = data.progress || 0;

      if (data.status === 1 && progress.value < 100) {
        currentStatus.value = 'processing';
        saveState();
      } else if (progress.value >= 100 || data.status === 100) {
        currentStatus.value = 'done';
        progress.value = 100;
        clearTimers();
        saveState();
        // Auto hide done status after 3 seconds
        // setTimeout(() => {
        //   resetState();
        // }, 3000);
      } else {
        saveState();
      }
    } catch (error) {
      console.error('Failed to fetch batch job status:', error);
      // Continue polling if API fails
    }
  };
  
  // Execute immediately once
  await checkStatus();
  
  // Poll every 6 seconds
  pollingTimer = setInterval(checkStatus, TIMEOUT + 1000);
};

// Handle queue update event
const handleQueueUpdate = (data) => {
  // console.log('Received queue update event:', data);
  resetState();
  
  isVisible.value = true;
  currentStatus.value = 'queue';
  progress.value = 0;
  jobId.value = data?.jobId || 'queue-job-item';
  
  saveState();
  
  // Switch to processing status after 1 second
  statusTimer = setTimeout(() => {
    saveState();
    
    // Start polling
    if (jobId.value) {
      startPolling();
    }
  }, TIMEOUT);
};

// Handle close
const handleClose = () => {
  resetState();
};

onMounted(() => {
  if (store.state.user_data.is_motom_internal_user) {
    // Restore previous state
    restoreState();
    
    // Listen to global event via EventBus
    EventBus.on('ready-queue-update', handleQueueUpdate);
  }
});

onUnmounted(() => {
  if (store.state.user_data.is_motom_internal_user) {
    // Save current state
    saveState();
    
    // Remove event listener from EventBus
    EventBus.off('ready-queue-update', handleQueueUpdate);
    
    // Clear timers
    clearTimers();
  }
});
</script>

<style lang="scss" scoped>
.queue-progress-container {
  
  &.static .queue-progress-card {
    position: relative;
    transform: none;
    margin-right: 0;
  }
}

.queue-progress-card {
  display: flex;
  align-items: center;
  gap: 12px;
  background: white;
  border-radius: 6px;
  padding: 8px 28px 8px 12px;
  box-shadow: 0 0 2px 0 rgba(0, 0, 0, 0.20), 0 2px 10px 0 rgba(0, 0, 0, 0.10);
  position: absolute;
  margin: 0 16px;
  transform: translateY(-48%);
  
  .icon-container {
    flex-shrink: 0;
    
    .icon {
      width: 24px;
      height: 24px;
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      
      &.icon-queue {
        background: var(--dark-blue);
        color: white;

        svg {
          height: 20px;
          width: 20px;
        }
      }
      
      &.icon-processing {
        background: var(--dark-blue);
        color: white;
        position: relative;

        svg {
          height: 20px;
          width: 20px;

          &:last-child {
            position: absolute;
            left: 50%;
            top: 50%;
            transform: translate(-50%, -50%);
          }

          &:first-child {
            animation: spin 3s linear infinite;
          }
        }
      }
      
      &.icon-done {
        background: var(--green);
        color: white;

        svg {
          height: 14px;
          width: 14px;
        }
      }
    }
  }
  
  .status-content {
    flex: 1;
    
    .status-title {
      font: var(--font-med-14);
      color: var(--black-90);
    }
    
    .description {
      font-size: 14px;
      color: var(--black-60);
    }
    
    .progress-bar {
      margin-top: 8px;
      height: 4px;
      background: #e5e7eb;
      border-radius: 2px;
      overflow: hidden;
      
      .progress-fill {
        height: 100%;
        background: #1e3a8a;
        border-radius: 2px;
        transition: width 0.3s ease;
      }
    }
  }
  
  .close-btn {
    position: absolute;
    top: 0;
    right: 0;
    background: transparent;
    border: none;
    color: var(--black-50);
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: all 0.2s ease;
    height: 24px;
    width: 24px;

    &:hover {
      color: var(--black-90);
    }

    svg {
      width: 9px;
      height: 9px;
      display: block;
    }
  }
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.slide-fade-enter-active {
  transition: all 0.3s ease;
}

.slide-fade-leave-active {
  transition: all 0.3s ease;
}

.slide-fade-enter-from {
  opacity: 0;
}

.slide-fade-leave-to {
  opacity: 0;
}
</style>
